import { ChangeEvent, FC, useEffect, useState, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useParams, Navigate, Link } from "react-router-dom";
import { Flex, Text, Box, VStack, Button } from "@chakra-ui/react";
import * as Sentry from "@sentry/react";
import { useFormik, FormikProvider } from "formik";
import { DateTime } from "luxon";
import * as Yup from "yup";
import { StringInput, SelectInput } from "~/components";
import {
  JUNCTION_HIPAA_AUTHORIZATION,
  TERMS_OF_SERVICE_URL,
  PRIVACY_POLICY_URL,
  sharedProps,
} from "~/constants";
import { routes } from "~/constants/routes";
import { Checkbox } from "~/OnboardingApp/components";
import { formatPhone } from "~/OnboardingApp/pages/questionPages/PhonePage/utils";
import { transformToFormattedDate } from "~/OnboardingApp/utils";
import { Trans } from "~/utils/i18next";
import { requiredOver18Date, requiredPhone } from "~/utils/validations";
import { ProductLogo, TwoPanelLayout } from "../components";
import { PRODUCT_MAPPING, ProductConfig } from "../constants";
import { FacilityFinderForm } from "./PatientFormPage/FacilityFinderForm";

const PatientFormPage: FC = () => {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const [errorMessage, setErrorMessage] = useState<ReactNode | null>(null);
  const [hasConfirmedLocation, setHasConfirmedLocation] = useState(false);

  const selectedProduct: ProductConfig | undefined = slug
    ? PRODUCT_MAPPING[slug]
    : undefined;

  // At-home tests don't need facility search
  const requiresFacilitySearch = slug !== "mens_basics";

  useEffect(() => {
    document.title = "Diagnostics - Patient Form";
  }, []);

  const validationSchema = Yup.object({
    firstName: Yup.string().required(
      t("diagnosticDashboard.patientForm.validation.firstNameRequired"),
    ),
    lastName: Yup.string().required(
      t("diagnosticDashboard.patientForm.validation.lastNameRequired"),
    ),
    gender: Yup.string().required(
      t("diagnosticDashboard.patientForm.validation.genderRequired"),
    ),
    bornOn: requiredOver18Date,
    email: Yup.string()
      .email(t("diagnosticDashboard.patientForm.validation.invalidEmail"))
      .required(t("diagnosticDashboard.patientForm.validation.emailRequired")),
    phoneNumber: requiredPhone,
    line1: Yup.string().required(
      t("diagnosticDashboard.patientForm.validation.streetAddressRequired"),
    ),
    line2: Yup.string(),
    line3: Yup.string(),
    city: Yup.string().required(
      t("diagnosticDashboard.patientForm.validation.cityRequired"),
    ),
    state: Yup.string().required(
      t("diagnosticDashboard.patientForm.validation.stateRequired"),
    ),
    postalCode: Yup.string()
      .required(
        t("diagnosticDashboard.patientForm.validation.postalCodeRequired"),
      )
      .matches(/^\d{5}$/, "Postal code must be exactly 5 digits"),
    howDidYouHear: Yup.string().required("Please select how you heard about us"),
    hipaaConsent: Yup.boolean().oneOf(
      [true],
      t("diagnosticDashboard.patientForm.validation.hipaaRequired"),
    ),
    termsConsent: Yup.boolean().oneOf(
      [true],
      t("diagnosticDashboard.patientForm.validation.termsRequired"),
    ),
  });

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      gender: "",
      bornOn: "",
      email: "",
      phoneNumber: "",
      line1: "",
      line2: "",
      line3: "",
      city: "",
      state: "",
      postalCode: "",
      howDidYouHear: "",
      country: "US", // Hardcoded to US
      hipaaConsent: false,
      termsConsent: false,
    },
    validationSchema,
    onSubmit: async (values) => {
      // Guard against undefined product
      if (!selectedProduct) return;

      try {
        const { bornOn } = values;
        const dateOfBirth = DateTime.fromFormat(bornOn, "MM / dd / yyyy", {
          zone: "utc",
        });
        const formattedDate = dateOfBirth.isValid
          ? dateOfBirth.toISODate()
          : "";

        // Format phone number with +1 country code
        const formattedPhone = values.phoneNumber.replace(/\D/g, "");
        const phoneWithCountryCode = `+1${formattedPhone}`;

        const response = await fetch("/api/v1/diagnostics/registrations", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            first_name: values.firstName,
            last_name: values.lastName,
            gender: values.gender,
            email: values.email,
            phone: phoneWithCountryCode,
            birth_date: formattedDate,
            product_slug: selectedProduct.slug,
            line_1: values.line1,
            line_2: values.line2,
            line_3: values.line3,
            city: values.city,
            state: values.state,
            postal_code: values.postalCode,
            how_did_you_hear: values.howDidYouHear,
            country: "US", // Always hardcoded to US
          }),
        });

        if (response.ok) {
          const responseData = await response.json();

          if (responseData.stripe_checkout_url) {
            window.location.href = responseData.stripe_checkout_url;
          } else {
            throw new Error("Missing checkout URL in response");
          }
        } else {
          const errorData = await response.json();
          console.error("Registration error:", errorData);

          // Check for duplicate email error
          const emailErrors = errorData.errors?.["patient_profile.email"];
          const phoneErrors = errorData.errors?.["patient_profile.phone"];

          if (emailErrors?.includes("has already been taken")) {
            const errorNode = (
              <>
                This email is already registered.{" "}
                <Link
                  to={routes.diagnosticsSignIn}
                  style={{ textDecoration: "underline" }}
                >
                  Sign in instead
                </Link>
              </>
            );
            setErrorMessage(errorNode);
            return;
          }

          // Check for duplicate phone error
          if (phoneErrors?.includes("has already been taken")) {
            const errorNode = (
              <>
                This phone number is already registered.{" "}
                <Link
                  to={routes.diagnosticsSignIn}
                  style={{ textDecoration: "underline" }}
                >
                  Sign in instead
                </Link>
              </>
            );
            setErrorMessage(errorNode);
            return;
          }

          // Handle other errors
          const otherEmailErrors = emailErrors?.join(", ");
          const otherPhoneErrors = phoneErrors?.join(", ");
          const otherErrors = errorData.errors?.base?.join(", ");

          throw new Error(
            otherEmailErrors ||
            otherPhoneErrors ||
            otherErrors ||
            "Failed to create registration",
          );
        }
      } catch (error) {
        console.error("Error submitting form:", error);
        Sentry.captureException(error);
        if (!errorMessage) {
          // Only set error if not already set by duplicate check
          setErrorMessage(
            error instanceof Error
              ? error.message
              : "An error occurred while submitting the form. Please try again.",
          );
        }
      }
    },
    validateOnMount: true,
    validateOnChange: true,
  });

  const handleDateChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const oldValue = formik.values.bornOn;

    const formattedDate = transformToFormattedDate({ value, oldValue });

    formik.setFieldValue("bornOn", formattedDate, true);
  };

  const handlePhoneChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const oldValue = formik.values.phoneNumber || "";
    const newValue = formatPhone({ value, oldValue });

    formik.setFieldValue("phoneNumber", newValue);
  };

  // If no valid product, redirect using React Router
  if (!slug || !selectedProduct) {
    return <Navigate to="/fertility-planning" replace />;
  }

  const leftContent = (
    <>
      <Text
        fontSize={{ base: "2xl", lg: "4xl" }}
        fontWeight="bold"
        lineHeight="1.2"
        fontFamily="Moderat Extended"
      >
        {t("diagnosticDashboard.patientForm.heading")}
      </Text>
      <Text fontSize={{ base: "md", lg: "lg" }} color="fg-main">
        {t("diagnosticDashboard.patientForm.subheading")}
      </Text>

      <Box
        px={5}
        py={4}
        bg="white"
        borderRadius="lg"
        width="100%"
        mt={4}
        boxShadow="0px 2px 4px 0px rgba(0, 0, 0, 0.1)"
      >
        <Flex alignItems="center" gap="14px">
          <ProductLogo slug={selectedProduct!.slug} boxSize="45px" />
          <Box>
            <Text
              fontWeight="bold"
              fontSize="18px"
              color="th.black"
              lineHeight="1.4"
            >
              {selectedProduct!.name}
            </Text>
            <Text fontSize="18px" color="th.black" lineHeight="1.4">
              {selectedProduct!.price}
            </Text>
          </Box>
        </Flex>
      </Box>
    </>
  );

  const rightContent = requiresFacilitySearch && !hasConfirmedLocation ? (
    <FacilityFinderForm
      productSlug={selectedProduct.slug}
      onProceed={() => setHasConfirmedLocation(true)}
    />
  ) : (
    <FormikProvider value={formik}>
      <VStack
        as="form"
        onSubmit={(e: React.FormEvent<HTMLDivElement>) => {
          e.preventDefault();
          formik.handleSubmit();
        }}
        w="full"
        alignItems="flex-start"
        spacing={6}
      >
        {/* Form content */}
        <Text fontSize="xl" fontWeight="bold">
          {t("diagnosticDashboard.patientForm.patientInfo")}
        </Text>

        {errorMessage && (
          <Box
            bg="red.100"
            color="red.700"
            p={3}
            borderRadius="md"
            width="100%"
          >
            {errorMessage}
          </Box>
        )}

        <Flex gap={4} width="100%" direction={{ base: "column", md: "row" }}>
          <Box flex={1}>
            <StringInput
              id="firstName"
              label={t("diagnosticDashboard.patientForm.labels.firstName")}
              placeholder={t(
                "diagnosticDashboard.patientForm.placeholders.firstName",
              )}
            />
          </Box>

          <Box flex={1}>
            <StringInput
              id="lastName"
              label={t("diagnosticDashboard.patientForm.labels.lastName")}
              placeholder={t(
                "diagnosticDashboard.patientForm.placeholders.lastName",
              )}
            />
          </Box>
        </Flex>

        <Flex gap={4} width="100%" direction={{ base: "column", md: "row" }}>
          <Box flex={1}>
            <SelectInput
              id="gender"
              label={t("diagnosticDashboard.patientForm.labels.gender")}
              placeholder={t(
                "diagnosticDashboard.patientForm.placeholders.selectGender",
              )}
              options={[
                {
                  value: "female",
                  label: t("diagnosticDashboard.patientForm.options.female"),
                },
                {
                  value: "male",
                  label: t("diagnosticDashboard.patientForm.options.male"),
                },
              ]}
            />
          </Box>

          <Box flex={1}>
            <StringInput
              id="bornOn"
              label={t("diagnosticDashboard.patientForm.labels.dateOfBirth")}
              placeholder={t(
                "diagnosticDashboard.patientForm.placeholders.dateOfBirth",
              )}
              onChange={handleDateChange}
            />
          </Box>
        </Flex>

        <Text fontSize="xl" fontWeight="bold" mt={2}>
          {t("diagnosticDashboard.patientForm.contactInfo")}
        </Text>

        <Box width="100%">
          <StringInput
            id="email"
            label={t("diagnosticDashboard.patientForm.labels.email")}
            placeholder={t(
              "diagnosticDashboard.patientForm.placeholders.email",
            )}
          />
          <Text fontSize="xs" color="gray.500" mt={1}>
            {t("diagnosticDashboard.patientForm.labels.emailHint")}
          </Text>
        </Box>

        <Box width="100%">
          <StringInput
            id="phoneNumber"
            label={t("diagnosticDashboard.patientForm.labels.phoneNumber")}
            placeholder={t(
              "diagnosticDashboard.patientForm.placeholders.phoneNumber",
            )}
            onChange={handlePhoneChange}
          />
        </Box>

        <Text fontSize="xl" fontWeight="bold" mt={2}>
          {t("diagnosticDashboard.patientForm.addressInfo")}
        </Text>

        <Box width="100%">
          <StringInput
            id="line1"
            label={t("diagnosticDashboard.patientForm.labels.streetAddress")}
            placeholder={t(
              "diagnosticDashboard.patientForm.placeholders.streetAddress",
            )}
          />
        </Box>

        <Box width="100%">
          <StringInput
            id="line2"
            label={t("diagnosticDashboard.patientForm.labels.apartment")}
            placeholder={t(
              "diagnosticDashboard.patientForm.placeholders.apartment",
            )}
          />
        </Box>

        <Flex gap={4} width="100%" direction={{ base: "column", md: "row" }}>
          <Box flex={1}>
            <StringInput
              id="city"
              label={t("diagnosticDashboard.patientForm.labels.city")}
              placeholder={t(
                "diagnosticDashboard.patientForm.placeholders.city",
              )}
            />
          </Box>

          <Box flex={1}>
            <SelectInput
              id="state"
              label={t("diagnosticDashboard.patientForm.labels.state")}
              placeholder={t(
                "diagnosticDashboard.patientForm.placeholders.state",
              )}
              options={[
                { value: "AL", label: "Alabama" },
                { value: "AK", label: "Alaska" },
                { value: "AZ", label: "Arizona" },
                { value: "AR", label: "Arkansas" },
                { value: "CA", label: "California" },
                { value: "CO", label: "Colorado" },
                { value: "CT", label: "Connecticut" },
                { value: "DE", label: "Delaware" },
                { value: "FL", label: "Florida" },
                { value: "GA", label: "Georgia" },
                { value: "HI", label: "Hawaii" },
                { value: "ID", label: "Idaho" },
                { value: "IL", label: "Illinois" },
                { value: "IN", label: "Indiana" },
                { value: "IA", label: "Iowa" },
                { value: "KS", label: "Kansas" },
                { value: "KY", label: "Kentucky" },
                { value: "LA", label: "Louisiana" },
                { value: "ME", label: "Maine" },
                { value: "MD", label: "Maryland" },
                { value: "MA", label: "Massachusetts" },
                { value: "MI", label: "Michigan" },
                { value: "MN", label: "Minnesota" },
                { value: "MS", label: "Mississippi" },
                { value: "MO", label: "Missouri" },
                { value: "MT", label: "Montana" },
                { value: "NE", label: "Nebraska" },
                { value: "NV", label: "Nevada" },
                { value: "NH", label: "New Hampshire" },
                { value: "NJ", label: "New Jersey" },
                { value: "NM", label: "New Mexico" },
                { value: "NY", label: "New York" },
                { value: "NC", label: "North Carolina" },
                { value: "ND", label: "North Dakota" },
                { value: "OH", label: "Ohio" },
                { value: "OK", label: "Oklahoma" },
                { value: "OR", label: "Oregon" },
                { value: "PA", label: "Pennsylvania" },
                { value: "SC", label: "South Carolina" },
                { value: "SD", label: "South Dakota" },
                { value: "TN", label: "Tennessee" },
                { value: "TX", label: "Texas" },
                { value: "UT", label: "Utah" },
                { value: "VT", label: "Vermont" },
                { value: "VA", label: "Virginia" },
                { value: "WA", label: "Washington" },
                { value: "WV", label: "West Virginia" },
                { value: "WI", label: "Wisconsin" },
                { value: "WY", label: "Wyoming" },
              ]}
            />
          </Box>
        </Flex>

        <Box width="100%">
          <StringInput
            id="postalCode"
            label={t("diagnosticDashboard.patientForm.labels.postalCode")}
            placeholder={t(
              "diagnosticDashboard.patientForm.placeholders.postalCode",
            )}
            maxLength={5}
          />
        </Box>

        <Box width="100%">
          <SelectInput
            id="howDidYouHear"
            label="How did you hear about us?"
            placeholder="Select an option"
            options={[
              { value: "Conceive coach", label: "Conceive coach" },
              { value: "Conceive member", label: "Conceive member" },
              { value: "Social media", label: "Social media" },
              { value: "An article or blog post", label: "An article or blog post" },
              { value: "A friend", label: "A friend" },
              { value: "Google search", label: "Google search" },
              { value: "Clinic or doctor", label: "Clinic or doctor" },
            ]}
          />
        </Box>

        <VStack width="100%" alignItems="flex-start" spacing={3} mt={4}>
          <Checkbox id="hipaaConsent" name="hipaaConsent">
            <Text fontSize="sm">
              <Trans
                components={{
                  HIPAALink: (
                    <Text
                      as="a"
                      color="inherit"
                      href={JUNCTION_HIPAA_AUTHORIZATION}
                      px={0}
                      target="_blank"
                      textDecoration="underline"
                      {...sharedProps.link.hover}
                    />
                  ),
                }}
                i18nKey="diagnosticDashboard.patientForm.hipaaConsent"
              />
            </Text>
          </Checkbox>

          <Checkbox id="termsConsent" name="termsConsent">
            <Text fontSize="sm">
              <Trans
                components={{
                  TermsLink: (
                    <Text
                      as="a"
                      color="inherit"
                      href={TERMS_OF_SERVICE_URL}
                      px={0}
                      target="_blank"
                      textDecoration="underline"
                      {...sharedProps.link.hover}
                    />
                  ),
                  PrivacyPolicyLink: (
                    <Text
                      as="a"
                      color="inherit"
                      href={PRIVACY_POLICY_URL}
                      px={0}
                      target="_blank"
                      textDecoration="underline"
                      {...sharedProps.link.hover}
                    />
                  ),
                  // TODO: Add Telehealth Informed Consent link
                  // TelehealthLink: (
                  //   <Text
                  //     as="a"
                  //     color="inherit"
                  //     href={TELEHEALTH_INFORMED_CONSENT_URL}
                  //     px={0}
                  //     target="_blank"
                  //     textDecoration="underline"
                  //     {...sharedProps.link.hover}
                  //   />
                  // ),
                }}
                i18nKey="diagnosticDashboard.patientForm.termsConsent"
              />
            </Text>
          </Checkbox>
        </VStack>

        <Button
          type="submit"
          size="th-md"
          variant="th-primary"
          width="100%"
          isLoading={formik.isSubmitting}
          disabled={formik.isSubmitting}
        >
          {t("diagnosticDashboard.patientForm.continueToCheckout")}
        </Button>
      </VStack>
    </FormikProvider>
  );

  return (
    <TwoPanelLayout leftContent={leftContent} rightContent={rightContent} />
  );
};

// eslint-disable-next-line import/no-default-export
export default PatientFormPage;
